export type HierarchyLevel = number;

// These hierarchy level names are taken from https://www.atlassian.com/agile/project-management/epics-stories-themes
export const ISSUE_HIERARCHY_LEVEL_GOAL: HierarchyLevel = 4;
export const ISSUE_HIERARCHY_LEVEL_STRATEGY: HierarchyLevel = 3;
export const ISSUE_HIERARCHY_LEVEL_INITIATIVE: HierarchyLevel = 2;
export const ISSUE_HIERARCHY_LEVEL_EPIC: HierarchyLevel = 1;
export const ISSUE_HIERARCHY_LEVEL_BASE: HierarchyLevel = 0;
export const ISSUE_HIERARCHY_LEVEL_SUBTASK: HierarchyLevel = -1;

export const BASE_HIERARCHY_TYPE = 'BASE' as const;
export const CHILD_HIERARCHY_TYPE = 'CHILD' as const;
export const PARENT_HIERARCHY_TYPE = 'PARENT' as const;
export const EPIC_PARENT_HIERARCHY_TYPE = 'EPIC' as const;

export type IssueTypeHierarchyLevelType =
	| typeof BASE_HIERARCHY_TYPE
	| typeof CHILD_HIERARCHY_TYPE
	| typeof PARENT_HIERARCHY_TYPE
	| typeof EPIC_PARENT_HIERARCHY_TYPE;

export const toHierarchyLevel = (level: number): HierarchyLevel => level;

/**
 * The "Base" level is a level that is not Subtask and is not Epic.
 * In Jira Software, this is the level that has Stories, Tasks, Bugs, ... in it
 *
 * In JSM, and Jira Core, and other products that do not support hierarchies:
 *   all issue types are considered the Base level
 *   and so this function will return `true` for null and undefined and NaN
 */
export const isBaseHierarchyLevel = (level?: HierarchyLevel | number | string | null): boolean => {
	const numericLevel = Number(level);
	if (Number.isNaN(numericLevel)) {
		return true;
	}
	return numericLevel === ISSUE_HIERARCHY_LEVEL_BASE;
};

/**
 * The "Epic" level is a level that is above Base
 * In Jira Software, this is the level that has Epic(s) in it
 *
 * In JSM, and Jira Core, and other products that do not support hierarchies:
 *   all issue types are considered the Base level
 *   and so this function will return `false` for null and undefined and NaN
 */
export const isEpicHierarchyLevel = (level?: HierarchyLevel | number | string | null): boolean => {
	const numericLevel = Number(level);
	if (Number.isNaN(numericLevel)) {
		return false;
	}
	return numericLevel === ISSUE_HIERARCHY_LEVEL_EPIC;
};

/**
 * The "Epic" level is a level that is above Base
 * In Jira Software, this is the level above Epic. e.g. level 2, level 3, etc.
 *
 * In JSM, and Jira Core, and other products that do not support hierarchies:
 *   all issue types are considered the Base level
 *   and so this function will return `false` for null and undefined and NaN
 */
export const isAboveEpicHierarchyLevel = (
	level?: HierarchyLevel | number | string | null,
): boolean => {
	const numericLevel = Number(level);
	if (Number.isNaN(numericLevel)) {
		return false;
	}
	return numericLevel > ISSUE_HIERARCHY_LEVEL_EPIC;
};

/**
 * The "Subtask" level is a level that is below Base
 * In Jira Software, this is the level that has Subtask(s) in it
 *
 * In JSM, and Jira Core, and other products that do not support hierarchies:
 *   all issue types are considered the Base level
 *   and so this function will return `false` for null and undefined and NaN
 */
export const isSubtaskHierarchyLevel = (
	level?: HierarchyLevel | number | string | null,
): boolean => {
	const numericLevel = Number(level);
	if (Number.isNaN(numericLevel)) {
		return false;
	}
	return numericLevel === ISSUE_HIERARCHY_LEVEL_SUBTASK;
};

export const getHierarchyLevelType = (hierarchyLevel: number): IssueTypeHierarchyLevelType => {
	if (Number(hierarchyLevel) > 0) {
		return PARENT_HIERARCHY_TYPE;
	}
	if (Number(hierarchyLevel) < 0) {
		return CHILD_HIERARCHY_TYPE;
	}
	return BASE_HIERARCHY_TYPE;
};

export const getHierarchyLevelTypeSplitEpicFromCustom = (
	hierarchyLevel: number,
): IssueTypeHierarchyLevelType => {
	if (Number(hierarchyLevel) === 1) {
		return EPIC_PARENT_HIERARCHY_TYPE;
	}
	if (Number(hierarchyLevel) > 1) {
		return PARENT_HIERARCHY_TYPE;
	}
	if (Number(hierarchyLevel) < 0) {
		return CHILD_HIERARCHY_TYPE;
	}
	return BASE_HIERARCHY_TYPE;
};
